<template>
  <div class="w-container w-full h-full flex flex-col">
    <div class="relative my-10 smd:my-14 flex flex-col flex-wrap gap-4 lg:flex-row">
      <search-input
        v-model="state.currentSearch"
        :placeholder="$t('models.model_search_form.collections')"
        @search="handleSearch"
      />
      <div class="flex flex-wrap gap-2">
        <span v-for="(tag, index) in limitedpinnedTags" :key="index"
          class="flex rounded-[24px] px-4 leading-[30px] items-center cursor-pointer text-14px"
          :class="{
            'bg-[#A3008E1A] border border-[#FFD4FA] hover:bg-[#A3008E4D]': index < 3,
            'bg-[#4B4B4B] hover:bg-grey-2': index >= 3,
          }"
          @click="state.currentSearch = tag; handleSearch()">
          {{ tag }}
        </span>
      </div>
    </div>

    <div v-if="state.currentSearch == '' && state.featuredSeries.length > 0" class="mb-20">
      <div class="text-18px font-medium leading-6">
        {{ $t('series.index.featured_collections')}}
      </div>

      <hr class="mt-4 border-gray-500"></hr>

      <div class="grid lg:grid-cols-2 gap-8 smd:gap-10 lg:gap-6 mt-6 mb-8">
        <featured-serie-item v-for="serie in state.featuredSeries" :key="serie.id" :serie="serie" :current_profile="state.current_profile"/>
      </div>
    </div>

    <div class="text-18px font-medium leading-6">
      {{ $t('series.index.all_collections')}}
    </div>

    <hr class="mt-4 border-gray-500">

    <template v-if="items.length > 0">
      <div class="grid lg:grid-cols-2 gap-8 smd:gap-10 lg:gap-6 xl:gap-12 xl:grid-cols-1 mt-6 mb-8">
        <serie-item v-for="serie in items" :key="serie.id" :serie="serie" :current_profile="state.current_profile"/>
      </div>
    </template>
    <template v-else-if="!state.initialLoading && !state.loading">
      <div class="w-full flex-1 flex flex-col justify-center items-center text-center text-20px">
        <img src="../../images/icons/search.svg" alt="search" class="w-12 h-12 mb-8"/>
        {{ $t('series.index.not_found')}}
      </div>
    </template>
  </div>
</template>

<script>

import { defineComponent, ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import debounce from 'lodash/debounce';

export default defineComponent({
  props: ['pinnedTags'],
  setup(props) {
    const state = reactive({
      loading: false,
      initialLoading: true,
      itemsCount: 12,
      currentSearch: '',
      series: [],
      featuredSeries: [],
      current_profile: null,
      totalCount: 0,
      currentPage: 1,
      hasMore: true,
    });

    const items = computed(() => state.series);

    const limitedpinnedTags = computed(() => {
      return props.pinnedTags.slice(0, 8);
    });

    const resetState = () => {
      state.currentPage = 1;
      state.series = [];
      state.featuredSeries = [];
      state.hasMore = true;
      state.loading = false;
    };

    const getSeries = async () => {
      const url = `/api/v1/series`;

      const params = new URLSearchParams({
        page: state.currentPage,
        per_page: state.itemsCount,
        kw: state.currentSearch
      });

      try {
        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          const data = await response.json();
          state.current_profile = data.current_profile;
          state.totalCount = data.pagination.total_count;

          const merged = [...state.series, ...data.data];
          state.series = Array.from(new Map(merged.map(item => [item.id, item])).values());

          const mergedFeatured = [...state.featuredSeries, ...data.featured_series];
          state.featuredSeries = Array.from(new Map(mergedFeatured.map(item => [item.id, item])).values());

          state.hasMore = state.currentPage < data.pagination.total_pages;
          if (state.hasMore) {
            state.currentPage++;
          }
        }
      } catch (error) {
        console.error('Error fetching series:', error);
      } finally {
        state.loading = false;
        state.initialLoading = false;
      }
    };

    const fetchItemData = async () => {
      if (!state.hasMore || state.loading) return;

      state.loading = true;
      await getSeries();
    };

    const handleSearch = async () => {
      resetState();
      await fetchItemData();
    };

    const handleScroll = debounce(() => {
      if (!state.hasMore || state.loading) return;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const scrolledPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
      if (scrolledPercentage > 30) {
        fetchItemData();
      }
    }, 100);

    onMounted(async () => {
      await fetchItemData();
      document.addEventListener('scroll', handleScroll, { passive: true });
    });

    onBeforeUnmount(() => {
      document.removeEventListener('scroll', handleScroll);
    });

    return {
      items,
      state,
      limitedpinnedTags,
      handleSearch
    };
  }
});
</script>
